import { useCallback, useEffect, useState } from "react";

import BannerModal from "./BannerModal";
import BannerView from "./BannerView";
import { BannerContent } from "./types";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

export enum BannerPortalName {
  AgentPortal = "agent-portal",
  D2C = "d2c",
  Maple = "maple",
}
interface BannerItem {
  title: {
    rendered: string;
  };
  content: {
    rendered: string;
    protected: boolean;
  };
}

interface Props {
  portalName: BannerPortalName;
  onBannerVisibilityChange?(visible: boolean): void;
  userGroups?: string[];
}

export const Banner = ({
  portalName,
  onBannerVisibilityChange,
  userGroups,
}: Props) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [bannerContent, setBannerContent] = useState<
    BannerContent | undefined
  >();

  const openModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  useEffect(() => {
    if (isDesktop && !bannerContent) {
      (async () => {
        try {
          const userGroupsSlugParam = userGroups
            ? `&banner_user_group_slug=${userGroups
                ?.map((userGroup) => userGroup.replace(/_/g, "-"))
                .join(",")}`
            : "";

          const response = await fetch(
            `https://covertree.com/wp-json/wp/v2/banner?per_page=1&banner_portal_slug=${portalName}${userGroupsSlugParam}`
          );

          const banners = (await response.json()) as BannerItem[];

          if (banners.length) {
            setBannerContent({
              title: banners[0].title.rendered,
              content: banners[0].content.rendered,
            });
          } else {
            setBannerContent(undefined);
          }
        } catch (e) {
          console.error(e);
        }
      })();
    }
  }, [portalName, userGroups, isDesktop, bannerContent]);

  useEffect(() => {
    if (onBannerVisibilityChange) {
      onBannerVisibilityChange(!!bannerContent);
    }
  }, [bannerContent, onBannerVisibilityChange]);

  if (!bannerContent || !isDesktop) {
    return null;
  }

  return (
    <>
      <BannerView
        title={bannerContent.title}
        onReadMore={!!bannerContent?.content ? openModal : undefined}
        sx={
          bannerContent
            ? {
                "& + div .MuiTableContainer-root": {
                  maxHeight: `calc(100vh - 330px)`,
                },
                "& + div #table-toolbar-container + div .MuiTableContainer-root":
                  {
                    maxHeight: `calc(100vh - 370px)`,
                  },
                "& + header#page-top-bar + div .MuiTableContainer-root": {
                  maxHeight: `calc(100vh - 418px)`,
                },
                "& + header#page-top-bar + div #table-toolbar-container + div .MuiTableContainer-root":
                  {
                    maxHeight: `calc(100vh - 458px)`,
                  },
              }
            : undefined
        }
      />
      <BannerModal
        bannerContent={bannerContent}
        onClose={closeModal}
        open={isModalOpen}
      />
    </>
  );
};
